import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import * as userApi from "@/api/endpoints/user"
import * as industryApi from "@/api/endpoints/industry"
import type { GetIndustriesParams } from "@/api/endpoints/industry"
import { resolveEnabled, type QueryOptions } from "./query-helpers"

export const userKeys = {
  all: ["user"] as const,
  profile: () => [...userKeys.all, "profile"] as const,
  industries: () => [...userKeys.all, "industries"] as const,
  industriesList: (params: GetIndustriesParams) =>
    [...userKeys.industries(), params] as const,
}

export const useGetUserQuery = (_params?: unknown, options?: QueryOptions) =>
  useQuery({
    queryKey: userKeys.profile(),
    queryFn: userApi.getUser,
    enabled: resolveEnabled(options),
    staleTime: 2 * 60 * 1000, // 2 min – avoid refetch storm in guards
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false, // Don't retry on error to prevent infinite loops
  })

export const useGetIndustriesQuery = (
  params?: GetIndustriesParams,
  options?: QueryOptions
) =>
  useQuery({
    queryKey:
      params?.page != null
        ? userKeys.industriesList(params)
        : userKeys.industries(),
    queryFn: () => industryApi.getIndustries(params),
    placeholderData: keepPreviousData,
    enabled: resolveEnabled(options),
  })

export const useCreateIndustryMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (name: string) => industryApi.createIndustry(name),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: userKeys.industries() })
    },
  })
}

export const useUpdateIndustryMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      industryApi.updateIndustry(id, name),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: userKeys.industries() })
    },
  })
}
